import COPY from "./content/siteCopy.json";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import SeoHead from "./SiteSeo";
import SiteLayout from "./SiteLayout";
import { CalendlyLink, SmartLink } from "./SiteHeader";
import { FinalCta } from "./sections";
import { toolBySlug } from "./calculators/tools";
import { usePlaybooks } from "./playbookData";
import { BudgetBoostBand } from "./BudgetBoostPromo";
import { VaultCard, fmtDate } from "./VaultHub";
import { MATT } from "./ExperiencePage";
import ComingSoonPage from "./ComingSoonPage";
import UnavailablePage from "./UnavailablePage";
import { getPublic } from "./publicFetch";
import ClosingChecklist from "./ClosingChecklist";

const API = process.env.REACT_APP_BACKEND_URL;
const PLAYBOOK_BY_TOPIC = COPY.vault.playbook_by_topic;
const SOLUTION_BY_TOPIC = COPY.vault.solution_by_topic;
const clean = (h) => ({ __html: DOMPurify.sanitize(h || "", { FORBID_TAGS: ["script", "style", "iframe"], ADD_ATTR: ["target"] }) });
const absImg = (src) => (src && src.startsWith("/api") ? `${API}${src}` : src);

const LINK_RX = /\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]*)\)/g;
function Inline({ text }) {
  const out = [];
  let last = 0, m;
  LINK_RX.lastIndex = 0;
  while ((m = LINK_RX.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<SmartLink key={m.index} href={m[2]}>{m[1]}</SmartLink>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.map((part, i) => (typeof part === "string" ? part.split(/(\*\*[^*]+\*\*)/g).map((b, j) => (b.startsWith("**") ? <strong key={`${i}-${j}`}>{b.slice(2, -2)}</strong> : b)) : part));
}
function Sections({ sections }) {
  return sections.map((s, i) => (
    <React.Fragment key={i}>
      {s.heading && <h2>{s.heading}</h2>}
      {(s.paragraphs || []).map((p, j) => (p.startsWith("- ") ? <ul key={j}>{p.split(/\n?- /).filter(Boolean).map((li, k) => <li key={k}><Inline text={li} /></li>)}</ul> : <p key={j}><Inline text={p} /></p>))}
    </React.Fragment>
  ));
}

export default function VaultArticle() {
  const slug = useLocation().pathname.split("/").filter(Boolean)[1];
  const [post, setPost] = useState(undefined);
  const playbooks = usePlaybooks();
  useEffect(() => {
    setPost(undefined);
    const preview = new URLSearchParams(window.location.search).get("preview");
    getPublic(`/vault/${slug}${preview ? `?preview=${encodeURIComponent(preview)}` : ""}`, { credentials: "include" }).then((r) => setPost(r.data || (r.error ? "error" : null)));
  }, [slug]);
  if (post === undefined) return <SiteLayout loading><section className="calc-hero"><div className="wrap"><div className="eyebrow">Strategy Vault</div><h1>Opening the Vault…</h1></div></section></SiteLayout>;
  if (post === null) return <ComingSoonPage />;
  if (post === "error") return <UnavailablePage title={`${slug.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())} · Strategy Vault · Hello Mortgage Team`} />;

  const calc = toolBySlug(post.calculator);
  const playbook = (playbooks || []).find((p) => p.slug === PLAYBOOK_BY_TOPIC[post.topic]);
  const solution = SOLUTION_BY_TOPIC[post.topic];
  const hero = absImg(post.hero_image);
  const path = `/strategy-vault/${post.slug}/`;
  const ld = { "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title, description: post.meta_description, datePublished: post.published_at, dateModified: post.updated_at || post.published_at, image: hero || undefined, articleSection: post.topic_name, inLanguage: "en-CA",
    author: { "@type": "Person", name: post.author }, publisher: { "@type": "Organization", name: "Hello Mortgage Team", logo: { "@type": "ImageObject", url: "https://www.hellomortgage.ca/brand/hello-mortgage-team-logo-transparent.png" } },
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.hellomortgage.ca${path}` } };

  return (
    <SiteLayout>
      <SeoHead title={post.seo_title || `${post.title} | Strategy Vault`} description={post.meta_description} path={path} ogImage={hero || undefined} />
      <Helmet><script type="application/ld+json">{JSON.stringify(ld)}</script></Helmet>
      <article>
        <section className="calc-hero vault-article-hero" data-testid="vault-article">
          <div className="wrap">
            <nav className="vault-crumbs" aria-label="Breadcrumb"><SmartLink href="/strategy-vault/">Strategy Vault</SmartLink><span>/</span><SmartLink href={`/strategy-vault/topic/${post.topic}/`} data-testid="vault-article-topic">{post.topic_name}</SmartLink></nav>
            <h1 data-testid="vault-article-title">{post.title}</h1>
            <p className="vault-byline" data-testid="vault-article-byline">{post.hero_kicker === "Weekly Market Brief" && <span className="vault-kicker">Weekly Market Brief</span>}{post.author} · {fmtDate(post.published_at)} · {post.reading_minutes} min read</p>
          </div>
        </section>
        <section className="vault-article-body">
          <div className="wrap solution-grid">
            <div className="vault-prose" data-testid="vault-article-content">
              {hero && !/<img/i.test(post.content_html || "") && <img src={hero} alt="" className={`vault-hero-img${post.hero_image?.startsWith("/brand/") ? " is-illo" : ""}`} onError={(e) => { e.currentTarget.style.display = "none"; }} />}
              {post.widget === "closing-checklist" && post.sections?.length ? (
                <>
                  <Sections sections={post.sections.slice(0, 1)} />
                  <ClosingChecklist rules={post.checklist || []} />
                  <Sections sections={post.sections.slice(1)} />
                </>
              ) : post.sections?.length ? <Sections sections={post.sections} /> : <div dangerouslySetInnerHTML={clean(post.content_html)} />}
              {post.matt_take && (
                <aside className="vault-take" data-testid="vault-matt-take">
                  <img src={MATT.photo} alt="Matt Broom-Hall" width="72" height="72" loading="lazy" />
                  <div>
                    <span className="tag">Matt's take</span>
                    <p className="vault-take-line">{post.matt_take}</p>
                    <p className="vault-take-by"><strong>{MATT.name}</strong> · {MATT.role} · <SmartLink href="/why-hello/#team" data-testid="vault-matt-take-link">Say hello →</SmartLink></p>
                  </div>
                </aside>
              )}
              {post.verdict && <div className="vault-verdict"><span className="tag">Our take</span><p>{post.verdict}</p></div>}
              {post.cta?.title && <div className="vault-cta" data-testid="vault-article-cta"><span className="tag">This week’s move</span><h3>{post.cta.title}</h3>{post.cta.subtitle && <p>{post.cta.subtitle}</p>}<CalendlyLink className="btn" testId="vault-article-cta-btn">Let's Talk Strategy</CalendlyLink></div>}
              <div className="vault-prevnext">
                {post.prev && <SmartLink href={`/strategy-vault/${post.prev.slug}/`} className="prev" data-testid="vault-prev">← Older<strong>{post.prev.title}</strong></SmartLink>}
                {post.next && <SmartLink href={`/strategy-vault/${post.next.slug}/`} className="next" data-testid="vault-next">Newer →<strong>{post.next.title}</strong></SmartLink>}
              </div>
            </div>
            <aside className="solution-rail">
              {calc && <SmartLink className="rail-card rail-calc" href={`/calculators/${calc.slug}/`} data-testid="vault-rail-calculator"><span className="tag">{calc.name} calculator</span><h3>{calc.tagline}</h3><p>{calc.blurb}</p><span className="tool-link">Run the numbers →</span></SmartLink>}
              {playbook && <SmartLink className="rail-card rail-calc rail-playbook" href={`/playbooks/${playbook.slug}/`} data-testid="vault-rail-playbook">{playbook.cover_url && <img src={`${API}${playbook.cover_url}`} alt={`${playbook.label} — free Hello Mortgage playbook cover`} loading="lazy" />}<div><span className="tag">Free playbook · {playbook.pages} pages</span><h3>{playbook.label}</h3><span className="tool-link">Read & download →</span></div></SmartLink>}
              {solution && <SmartLink className="rail-card rail-calc" href={solution[0]} data-testid="vault-rail-solution"><span className="tag">Go deeper</span><h3>{solution[1]}</h3><span className="tool-link">See how we help →</span></SmartLink>}
              <div className="rail-card rail-talk"><h3>{COPY.vault.talk_heading}</h3><p>{COPY.vault.talk_text}</p><CalendlyLink className="btn" testId="vault-rail-cta">Let's Get This Going</CalendlyLink></div>
            </aside>
          </div>
        </section>
      </article>
      {post.topic === "first-time-buyers" && <BudgetBoostBand />}
      {post.related?.length > 0 && (
        <section className="vault-related"><div className="wrap">
          <div className="intro"><div><div className="eyebrow">Keep reading</div><h2 className="display">{post.topic === "hot-takes" ? "More hot takes." : `More on ${post.topic_name.toLowerCase()}.`}</h2></div><p>{post.topic_blurb}</p></div>
          <div className="vault-grid" data-testid="vault-related">{post.related.map((p) => <VaultCard key={p.slug} p={p} />)}</div>
          <p style={{ marginTop: 28 }}><SmartLink className="textlink" href={`/strategy-vault/topic/${post.topic}/`}>Everything on {post.topic_name} →</SmartLink></p>
        </div></section>
      )}
      <FinalCta />
    </SiteLayout>
  );
}
